import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { telemetry } from "@/lib/telemetry";
import { statusOf } from "@/lib/api-error";

/**
 * O cache de dados da aplicação inteira.
 *
 * Morava dentro do `App.tsx`, inalcançável de fora do React. O `authStore` é um
 * store zustand — vive fora da árvore de componentes e não tem `useQueryClient()`
 * — e precisa **esvaziar este cache na troca de sessão**: sem isso a próxima
 * pessoa a entrar via os dados da anterior até o cache vencer ou alguém dar F5.
 *
 * Um módulo só, importado pelos dois. Não é global disfarçada: já era uma
 * instância única, só que trancada num arquivo que ninguém mais podia importar.
 */
/**
 * Os três `4xx` que **não** são veredito, e por isso seguem a regra geral.
 *
 * `408` é timeout e `425` é "cedo demais, manda de novo": os dois dizem que a
 * primeira tentativa não chegou a virar resposta. O `429` diz, no corpo, para
 * tentar mais tarde — negar a segunda tentativa é desobedecer o que ele pede.
 *
 * São exceção porque a checagem é de **faixa**, não de lista: qualquer outro
 * `4xx` — `405`, `409`, `410`, `413` — é veredito e continua sem repetir. Trocar
 * a faixa por uma lista de vereditos faria o `409` passar a repetir, e ele não
 * deve (`query-retry-policy.test.ts` já afirma isso).
 *
 * ⚠️ **Hoje nenhuma consulta alcança um `429`.** Os 15 `@limiter.limit` da api
 * estão todos em escritas, e as três exceções (`/api/auth/entra/login`,
 * `/api/auth/entra/callback` e o callback do ADT) são navegação de página, que
 * não passa por aqui; `retry` também só está configurado para `queries`, nunca
 * para `mutations`. O `429` está na lista pelo dia em que um limitador cair numa
 * rota de leitura: ela herdaria a política daqui **em silêncio**, e ninguém
 * revisa retry do frontend ao mexer num decorator do backend.
 */
const RETRYABLE_4XX = new Set([408, 425, 429]);

/**
 * Vale a pena perguntar de novo?
 *
 * Só quando a primeira resposta não foi um veredito. `500`, `502`, timeout e
 * queda de rede podem mudar na segunda tentativa. Um `404`, `422` ou `403` não
 * muda: o servidor já disse, com convicção, que aquilo não existe ou não é seu.
 * As três exceções dentro da faixa `4xx` estão em `RETRYABLE_4XX`.
 *
 * Era `retry: 1` para tudo, e o custo aparecia no pior lugar — abrir um projeto
 * inexistente dispara sete consultas entre tela e casca, cada uma repetindo uma
 * vez, com o atraso exponencial do TanStack Query entre as duas. Catorze
 * requisições condenadas e a espera entre elas: era isso que fazia o redirect
 * demorar em vez de acontecer.
 *
 * O `401` está entre os que não repetem, e não é esquecimento: quem trata sessão
 * expirada é o interceptor do axios, que renova o token e refaz a requisição
 * original. Repetir aqui por cima seria uma segunda tentativa cega depois de o
 * interceptor já ter desistido.
 */
export function retryUnlessTheServerAlreadyAnswered(failureCount: number, error: unknown): boolean {
  const status = statusOf(error);
  if (status != null && status >= 400 && status < 500 && !RETRYABLE_4XX.has(status)) {
    return false;
  }
  return failureCount < 1;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: retryUnlessTheServerAlreadyAnswered,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      telemetry.captureError("query", error, { queryKey: String(query.queryKey[0]) });
    },
  }),
  mutationCache: new MutationCache({
    onError: (error) => {
      telemetry.captureError("mutation", error);
    },
  }),
});

/**
 * Joga fora tudo que o cache guarda. Chamado na entrada e na saída de sessão.
 *
 * `clear()` e não `invalidateQueries()`: invalidar apenas marca como velho e
 * dispara refetch, deixando o dado antigo na tela enquanto a chamada não volta —
 * que é exatamente o defeito. E o dado antigo aqui é de **outra pessoa**.
 *
 * `cancelQueries()` antes, e é o que evita a rajada: no logout os cookies já
 * foram apagados pelo `apiLogout()`, então toda requisição em voo vai voltar
 * `401`, cair no interceptor do axios, tentar `POST /api/auth/refresh` com o
 * cookie de refresh morto e terminar em `redirectToLogin` — que faz
 * `window.location.href`, um reload duro no lugar da navegação da SPA. São
 * requisições condenadas antes de serem enviadas.
 *
 * ⚠️ **O que isto não resolve, medido:** `clear()` remove as queries, e um
 * observer que **sobreviva** ao render seguinte reconstrói a chave e busca de
 * novo. Medi as duas sequências com um observer montado: `clear()` sozinho não
 * refaz a chamada; `clear()` mais um re-render refaz. No logout o re-render vem
 * do `set({user: null})`, e nesse mesmo commit o `AuthGuard` devolve
 * `<Navigate>` em vez dos filhos, então a subárvore protegida desmonta sem
 * renderizar. Quem sobrevive é query fora do guard — se um dia aparecer uma que
 * dispare aqui, o lugar de resolver é o `enabled` dela, não este módulo.
 */
export function resetQueryCache() {
  // Não é `await`: o cancelamento é sinal para quem está em voo, e o cache tem
  // de estar vazio quando esta linha devolver — quem chama troca a sessão em
  // seguida, e um `await` aqui deixaria dado da pessoa anterior na tela no meio.
  void queryClient.cancelQueries();
  queryClient.clear();
}
